<template>
  <v-container fluid pb-4>
    <v-row>
      <v-col>
        <v-card>
          <v-toolbar dark color="primary">
            <v-row>
              <v-col cols="5" sm="11" class="text-left">
                <v-toolbar-title>
                  {{ $t('Список торговых секций') }}
                </v-toolbar-title>
              </v-col>
              <v-col v-if="isAdmin" cols="1" class="text-right">
                <btn-icon-tip
                    icon="add"
                    icon-color="secondaryiconbtn"
                    :tip="$t('Добавить торговую секцию')"
                    @click="openAddDialog" />
              </v-col>
            </v-row>
          </v-toolbar>
          <general-error v-if="!!generalError" />
          <v-card-text>
            <v-data-table
                :headers="headers"
                :items="items"
                hide-default-footer
                disable-pagination
                disable-filtering
                item-key="productSectionId"
                :locale="systemLocale">
              <template v-slot:top>
                <v-progress-linear :active="isLoading" color="loader" indeterminate />
              </template>
              <!-- eslint-disable-next-line vue/valid-v-slot -->
              <template v-slot:item.nameRu="{ item }">
                {{ item.nameRu }}
              </template>
              <!-- eslint-disable-next-line vue/valid-v-slot -->
              <template v-slot:item.nameEn="{ item }">
                {{ item.nameEn }}
              </template>
              <!-- eslint-disable-next-line vue/valid-v-slot -->
              <template v-slot:item.isLocked="{ item }">
                <icon-tip
                    :icon="item.isLocked
                      ? 'lock'
                      : 'lock_open'"
                    :icon-color="item.isLocked
                      ? 'error'
                      : 'success'"
                    :tip="$t('Текущий статус: {0}', [ item.isLocked
                      ? $t('Заблокирован')
                      : $t('Активный') ] )" />
              </template>
              <!-- eslint-disable-next-line vue/valid-v-slot -->
              <template v-slot:item.productSectionId="{ item }">
                <btn-icon-tip
                    v-if="isAdmin"
                    icon="edit"
                    :tip="$t('Редактировать')"
                    @click="openEditDialog(item.productSectionId)" />
                <btn-icon-tip
                    v-if="isAdmin"
                    icon="delete"
                    icon-color="error"
                    :tip="$t('Удалить')"
                    @click="openConfirmDialog(item.productSectionId)" />
                <btn-icon-tip
                    icon="security"
                    :tip="$t('Допущенные организации')"
                    @click="openOrganizationsByProductSectionDialog(item.productSectionId)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <product-section-dialog
        v-if="productSectionDialog.show"
        :dialog-prop="productSectionDialog"
        @close="closeDialog('productSectionDialog')" />
    <organizations-by-product-section-dialog
        v-if="organizationsDialog.show"
        :dialog-prop="organizationsDialog"
        @close="closeDialog('organizationsDialog')" />
  </v-container>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import IconTip from '@/components/reused/ui/IconTip.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import ProductSectionDialog from './ProductSectionDialog.vue'
  import OrganizationsByProductSectionDialog from './OrganizationsByProductSectionDialog.vue'
  import { mapActions, mapState } from 'vuex'
  import { PRODUCT_SECTION_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      IconTip,
      ConfirmDialog,
      GeneralError,
      ProductSectionDialog,
      OrganizationsByProductSectionDialog
    },
    data () {
      return {
        isLoading: false,
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        },
        productSectionDialog: {
          show: false,
          id: null,
          callback: null
        },
        organizationsDialog: {
          show: false,
          id: null
        }
      }
    },
    computed: {
      ...mapState(PRODUCT_SECTION_MANAGEMENT.NAMESPACE, {
        productSections: state => (state[PRODUCT_SECTION_MANAGEMENT.STATE.PRODUCT_SECTIONS] || { })
      }),
      headers () {
        const headers = [
          { text: this.$t('Наименование (рус.)'), align: 'start', sortable: true, value: 'nameRu' },
          { text: this.$t('Наименование (англ.)'), align: 'start', sortable: true, value: 'nameEn' },
          { text: this.$t('Статус'), align: 'start', sortable: true, value: 'isLocked' },
          { text: this.$t('Действия'), align: 'center', sortable: false, value: 'productSectionId' }
        ]

        return headers
      },
      items () {
        return this.productSections.items || []
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.getProductSections()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(PRODUCT_SECTION_MANAGEMENT.NAMESPACE, {
        getProductSectionsAction: PRODUCT_SECTION_MANAGEMENT.ACTIONS.GET_PRODUCT_SECTIONS,
        editProductSectionAction: PRODUCT_SECTION_MANAGEMENT.ACTIONS.EDIT_PRODUCT_SECTION,
        addProductSectionAction: PRODUCT_SECTION_MANAGEMENT.ACTIONS.ADD_PRODUCT_SECTION,
        deleteProductSectionAction: PRODUCT_SECTION_MANAGEMENT.ACTIONS.DELETE_PRODUCT_SECTION,
        clearProductSectionsAction: PRODUCT_SECTION_MANAGEMENT.ACTIONS.CLEAR_PRODUCT_SECTIONS
      }),
      clearStore: async function () {
        return Promise.all(
          [
            this.clearProductSectionsAction()
          ])
      },
      openConfirmDialog: function (id) {
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Торговая секция будет удалена. Продолжить?')
        this.confirmDialog.callback = this.deleteProductSection
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      openAddDialog: function () {
        this.productSectionDialog.callback = this.addProductSection
        this.productSectionDialog.show = true
      },
      openEditDialog: function (id) {
        this.productSectionDialog.id = id
        this.productSectionDialog.callback = this.editProductSection
        this.productSectionDialog.show = true
      },
      openOrganizationsByProductSectionDialog: function (id) {
        this.organizationsDialog.id = id
        this.organizationsDialog.show = true
      },
      addProductSection: async function (section) {
        try {
          this.isLoading = true
          await this.addProductSectionAction(section)
          this.isLoading = false
          this.getProductSections()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      editProductSection: async function (section) {
        try {
          this.isLoading = true
          await this.editProductSectionAction(section)
          this.isLoading = false
          this.getProductSections()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      deleteProductSection: async function (id) {
        try {
          this.isLoading = true
          await this.deleteProductSectionAction({ id })
          this.isLoading = false
          this.getProductSections()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getProductSections: async function () {
        try {
          this.isLoading = true
          await this.getProductSectionsAction({
            limit: 0,
            offset: 0
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
